"use client"

import { Button } from "@/components/ui/button"
import { Download, Loader2, Check, Archive } from "lucide-react"
import { formatFileSize } from "@/lib/pdf-splitter-api"
import type { ChunkInfo } from "./types"

interface ChunkListProps {
  displayChunks: ChunkInfo[]
  isProcessing: boolean
  hasRealChunks: boolean
  isDownloading: boolean
  downloadProgress: string | null
  processingProgress: number
  onDownloadChunk: (chunk: ChunkInfo) => void
  onDownloadAll: () => void
}

export function ChunkList({
  displayChunks,
  isProcessing,
  hasRealChunks,
  isDownloading,
  downloadProgress,
  processingProgress,
  onDownloadChunk,
  onDownloadAll,
}: ChunkListProps) {
  if (displayChunks.length === 0) return null

  return (
    <>
      {isProcessing && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Processing chunks...</span>
            <span>{processingProgress}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${processingProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">
            {isProcessing ? "Processing Chunks" : "Generated Chunks"} ({displayChunks.length})
          </h3>
          {!isProcessing && hasRealChunks && (
            <Button
              onClick={onDownloadAll}
              variant="outline"
              size="sm"
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {downloadProgress || "Downloading..."}
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4 mr-2" />
                  Download All (ZIP)
                </>
              )}
            </Button>
          )}
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {displayChunks.map((chunk) => (
            <div
              key={chunk.chunkId}
              className={`flex items-center justify-between p-3 border rounded-lg transition-all duration-300 ${
                chunk.status === "ready"
                  ? "bg-muted/50 border-border"
                  : chunk.status === "processing"
                    ? "bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800"
                    : "bg-muted/20 border-dashed border-muted-foreground/30"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center h-10 w-10 rounded-full font-medium transition-all duration-300 ${
                    chunk.status === "ready"
                      ? "bg-primary/10 text-primary"
                      : chunk.status === "processing"
                        ? "bg-blue-100 text-blue-600 dark:bg-blue-900/50"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {chunk.status === "ready" ? (
                    <Check className="h-5 w-5" />
                  ) : chunk.status === "processing" ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    chunk.chunkNumber
                  )}
                </div>
                <div>
                  <p
                    className={`text-sm font-medium ${
                      chunk.status === "pending" ? "text-muted-foreground" : ""
                    }`}
                  >
                    {chunk.fileName || `Chunk ${chunk.chunkNumber}`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {chunk.status === "pending"
                      ? `Pages ${chunk.startPage}-${chunk.endPage} • Waiting...`
                      : chunk.status === "processing"
                        ? "Processing..."
                        : `Pages ${chunk.startPage}-${chunk.endPage} • ${formatFileSize(chunk.fileSizeBytes)}`}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => onDownloadChunk(chunk)}
                variant="outline"
                size="sm"
                disabled={chunk.status !== "ready"}
                className={chunk.status !== "ready" ? "opacity-50" : ""}
              >
                {chunk.status === "ready" ? (
                  <Download className="h-4 w-4" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
